// src/pages/DashboardPage/Dashboard.tsx

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Stack,
  Typography,
  LinearProgress,
  useTheme,
  IconButton,
  Tooltip,
  Modal,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  InputLabel,
  Chip,
} from "@mui/material";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  animate,
} from "framer-motion";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// MUI Icons
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

// Custom PNG Icons
import activeDeviceIcon from "../../assets/active-device.png";
import positiveCasesIcon from "../../assets/postive-cases.png";
import totalTestsIcon from "../../assets/All-tests.png";
import drugResistanceIcon from "../../assets/drug-resistance.png";
import ageIcon from "../../assets/age-distribution.png";
import outbreakIcon from "../../assets/outbreak-alert.png";

// Components
import MapComponent from "../Map/Map";
import LineChart from "../Charts/LineChart";
import PieChart from "../Charts/PieChart";

/* --------------------------------------------------------------- */
/*  Types                                                          */
/* --------------------------------------------------------------- */
interface HealthCenter {
  _id: string;
  name: string;
  district: string;
  lat: number;
  lng: number;
  status: "Active" | "Offline";
  battery: string;
}

interface Test {
  _id: string;
  deviceId: string;
  caseId: string;
  age: number;
  gender: "Male" | "Female";
  diagnosisResults: "Positive" | "Negative" | "Indeterminate";
  parasiteSpecies: string;
  parasiteDensity: {
    value?: number;
    ci?: [number, number];
  };
  drugResistance: string;
  healthCenter: string;
  location: string;
  timestamp: string;
}

/* --------------------------------------------------------------- */
/*  Animated Number                                                */
/* --------------------------------------------------------------- */
const AnimatedNumber: React.FC<{ value: number }> = ({ value }) => {
  const count = useMotionValue(value);
  const rounded = useTransform(count, (latest) =>
    Math.round(latest).toLocaleString()
  );

  useEffect(() => {
    const animation = animate(count, value, { duration: 1.2, ease: "easeOut" });
    return () => animation.stop();
  }, [count, value]);

  return <motion.span>{rounded}</motion.span>;
};

/* --------------------------------------------------------------- */
/*  Trend Indicator for Positive Cases                             */
/* --------------------------------------------------------------- */
const TrendIndicator: React.FC<{ value: number }> = ({ value }) => (
  <Stack direction="row" alignItems="center" spacing={0.5}>
    <TrendingUpIcon sx={{ color: "#5EC16A", fontSize: 20 }} />
    <Typography
      variant="subtitle1"
      fontWeight={700}
      sx={{ color: "#5EC16A", fontFamily: "'Nunito Sans', sans-serif" }}
    >
      +{value}%
    </Typography>
  </Stack>
);

/* --------------------------------------------------------------- */
/*  Motion Card Wrapper                                            */
/* --------------------------------------------------------------- */
const MotionCard: React.FC<{
  children: React.ReactNode;
  index?: number;
  clickable?: boolean;
  onClick?: () => void;
}> = ({ children, index = 0, clickable = false, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    whileHover={clickable ? { scale: 1.03 } : { scale: 1.02 }}
    style={{ cursor: clickable ? "pointer" : "default" }}
    onClick={onClick}
  >
    <Box
      sx={{
        borderRadius: 3,
        height: "100%",
        minHeight: 140,
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        bgcolor: "#273142",
        p: 3,
      }}
    >
      {children}
    </Box>
  </motion.div>
);

/* --------------------------------------------------------------- */
/*  Metric Cards Grid (Smaller, Fixed Size)                        */
/* --------------------------------------------------------------- */
const MetricsGrid: React.FC<{
  activeCenters: number;
  positiveCases: number;
  drugResistanceCases: number;
  totalTests: number;
  navigate: (path: string, options?: any) => void;
}> = ({ activeCenters, positiveCases, drugResistanceCases, totalTests, navigate }) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
        gap: 3,
        mb: 6,
      }}
    >
      {/* Active Devices */}
      <MotionCard index={0}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img src={activeDeviceIcon} alt="Active Devices" style={{ width: 28, height: 28 }} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
              Active Devices
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "white",
                fontFamily: "'Nunito Sans', sans-serif",
              }}
            >
              <AnimatedNumber value={activeCenters} />
            </Typography>
          </Box>
        </Stack>
      </MotionCard>

      {/* Positive Cases – Special Trend Design */}
      <MotionCard index={1} clickable onClick={() => navigate("/all-tests", { state: { filter: { status: "Positive" } } })}>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img src={positiveCasesIcon} alt="Positive Cases" style={{ width: 28, height: 28 }} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                Positive Cases
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{
                    color: "white",
                    fontFamily: "'Nunito Sans', sans-serif",
                  }}
                >
                  <AnimatedNumber value={positiveCases} />
                </Typography>
                <TrendIndicator value={8.5} />
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </MotionCard>

      {/* Drug Resistance */}
      <MotionCard index={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img src={drugResistanceIcon} alt="Drug Resistance" style={{ width: 28, height: 28 }} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
              Drug Resistance
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "white",
                fontFamily: "'Nunito Sans', sans-serif",
              }}
            >
              <AnimatedNumber value={drugResistanceCases} />
            </Typography>
          </Box>
        </Stack>
      </MotionCard>

      {/* Total Tests */}
      <MotionCard index={3} clickable onClick={() => navigate("/all-tests")}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img src={totalTestsIcon} alt="Total Tests" style={{ width: 28, height: 28 }} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
              Total Tests
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "white",
                fontFamily: "'Nunito Sans', sans-serif",
              }}
            >
              <AnimatedNumber value={totalTests} />
            </Typography>
          </Box>
        </Stack>
      </MotionCard>
    </Box>
  );
};

/* --------------------------------------------------------------- */
/*  Main Dashboard Component                                       */
/* --------------------------------------------------------------- */
const Dashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const [loading, setLoading] = useState(true);
  const [openMap, setOpenMap] = useState(false);
  const [districtFilter, setDistrictFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [healthCenters, setHealthCenters] = useState<HealthCenter[]>([]);
  const [tests, setTests] = useState<Test[]>([]);
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const [refreshing, setRefreshing] = useState(false);
  const [ageOpen, setAgeOpen] = useState(false);

  /* ---------- Data Fetch + Caching ---------- */
  const fetchAndCacheData = useCallback(async () => {
    setRefreshing(true);
    try {
      const [hcRes, testRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/health-centers`),
        fetch(`${API_BASE_URL}/api/tests`),
      ]);

      const hcData = await hcRes.json();
      const testData = await testRes.json();

      setHealthCenters(hcData);
      setTests(testData);

      const now = Date.now();
      sessionStorage.setItem("healthCenters", JSON.stringify(hcData));
      sessionStorage.setItem("tests", JSON.stringify(testData));
      sessionStorage.setItem("data_timestamp", now.toString());
      setLastUpdated(now);
    } catch (err) {
      console.error("Data fetch error:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    const cachedHC = sessionStorage.getItem("healthCenters");
    const cachedTests = sessionStorage.getItem("tests");
    const cachedTime = sessionStorage.getItem("data_timestamp");
    const now = Date.now();
    const isCacheValid = cachedTime && now - parseInt(cachedTime) < 60 * 60 * 1000;

    if (cachedHC && cachedTests && isCacheValid) {
      setHealthCenters(JSON.parse(cachedHC));
      setTests(JSON.parse(cachedTests));
      setLoading(false);
    } else {
      fetchAndCacheData();
    }
  }, [fetchAndCacheData]);

  /* ---------- Filtered Centers ---------- */
  const filteredCenters = useMemo(
    () =>
      healthCenters.filter(
        (c) =>
          (districtFilter === "All" || c.district === districtFilter) &&
          (statusFilter === "All" || c.status === statusFilter)
      ),
    [healthCenters, districtFilter, statusFilter]
  );

  /* ---------- Derived Metrics ---------- */
  const activeCenters = healthCenters.filter((c) => c.status === "Active").length;
  const totalTests = tests.length;
  const positiveCases = tests.filter((t) => t.diagnosisResults === "Positive").length;

  const drugResistanceCases = useMemo(() => {
    return tests.filter(
      (t) =>
        t.diagnosisResults === "Positive" &&
        (t.drugResistance?.toLowerCase().includes("resistance") ||
         t.drugResistance?.toLowerCase() === "yes")
    ).length;
  }, [tests]);

  /* ---------- Age Distribution ---------- */
  const ageDistribution = useMemo(() => {
    const bins = { "0-5": 0, "6-12": 0, "13-18": 0, "19+": 0 };

    tests.forEach((t) => {
      const age = t.age;
      if (age <= 5) bins["0-5"]++;
      else if (age <= 12) bins["6-12"]++;
      else if (age <= 18) bins["13-18"]++;
      else bins["19+"]++;
    });

    const totalWithAge = Object.values(bins).reduce((a, b) => a + b, 0);

    return {
      totalWithAge,
      entries: [
        { label: "0–5 years", count: bins["0-5"] },
        { label: "6–12 years", count: bins["6-12"] },
        { label: "13–18 years", count: bins["13-18"] },
        { label: "19+ years", count: bins["19+"] },
      ],
    };
  }, [tests]);

  /* ---------- Recent Activity ---------- */
  const recentActivity = useMemo(() => {
    const centerMap = new Map<string, string>();
    healthCenters.forEach((c) => centerMap.set(c.name, c.district));

    return [...tests]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 10)
      .map((test) => ({
        healthCenter: test.healthCenter,
        district: centerMap.get(test.healthCenter) || "Unknown",
        status: test.diagnosisResults,
        species: test.parasiteSpecies || "N/A",
        timestamp: dayjs(test.timestamp).format("MMM DD, YYYY HH:mm"),
      }));
  }, [tests, healthCenters]);

  /* ---------- Export to PDF ---------- */
  const exportDashboard = async () => {
    const element = document.getElementById("dashboard-root");
    if (!element) return;
    const canvas = await html2canvas(element);
    const pdf = new jsPDF("p", "mm", "a4");
    const imgData = canvas.toDataURL("image/png");
    pdf.addImage(imgData, "PNG", 10, 10, 190, 0);
    pdf.save("MariTest_Dashboard.pdf");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "80vh" }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress color="primary" />
          <Typography variant="body1" color="text.secondary">
            Loading Dashboard Data...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box id="dashboard-root" sx={{ p: { xs: 2, md: 3 }, minHeight: "100vh" }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={2}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              fontFamily: "'Work Sans', sans-serif",
              color: "#FFFFFF",
            }}
          >
            Dashboard
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Last synced at {dayjs(lastUpdated).format("HH:mm:ss")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Refresh Data">
            <span>
              <IconButton onClick={fetchAndCacheData} disabled={refreshing}>
                <RefreshIcon color={refreshing ? "disabled" : "primary"} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Export Dashboard PDF">
            <span>
              <IconButton onClick={exportDashboard}>
                <FileDownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Stack>

      {/* Smaller, Non-Sticky Metrics Cards */}
      <MetricsGrid
        activeCenters={activeCenters}
        positiveCases={positiveCases}
        drugResistanceCases={drugResistanceCases}
        totalTests={totalTests}
        navigate={navigate}
      />

      {/* Rest of Dashboard (Map, Charts, etc.) */}
      {/* Map + Right Column */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
          gap: { xs: 3, md: 4 },
          alignItems: "stretch",
          mb: 8,
        }}
      >
        {/* Map Section */}
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: "#FFFFFF" }}>
              Case Mapping
            </Typography>
            <Tooltip title="Expand Map View">
              <span>
                <IconButton onClick={() => setOpenMap(true)}>
                  <ExploreOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box
            sx={{
              height: { xs: "60vh", md: "80vh" },
              minHeight: { xs: 500, md: 600 },
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <MapComponent centers={filteredCenters} />
          </Box>
        </Box>

        {/* Right Column */}
        <Stack spacing={3}>
          {/* Gender Distribution */}
          <Box sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", bgcolor: "#273142", p: { xs: 2.5, sm: 3 } }}>
            <PieChart height={180} />
          </Box>

          {/* Age Distribution – Collapsible */}
          <Box sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", bgcolor: "#273142" }}>
            <Box
              onClick={() => setAgeOpen((prev) => !prev)}
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <img src={ageIcon} alt="Age Distribution" style={{ width: 18, height: 18 }} />
                <Typography fontSize={14} fontWeight={500} color="white">
                  Age Distribution
                </Typography>
              </Stack>

              <ExpandMoreIcon
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  fontSize: 20,
                  transform: ageOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.25s ease",
                }}
              />
            </Box>

            <AnimatePresence initial={false}>
              {ageOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                >
                  <Box sx={{ px: 2.5, pb: 2.5 }}>
                    {ageDistribution.entries.map((entry) => {
                      const pct =
                        ageDistribution.totalWithAge > 0
                          ? ((entry.count / ageDistribution.totalWithAge) * 100).toFixed(1)
                          : "0.0";

                      return (
                        <Box key={entry.label} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.4 }}>
                          <Typography sx={{ minWidth: 90, fontSize: 12, color: "rgba(255,255,255,0.65)" }}>
                            {entry.label}
                          </Typography>

                          <LinearProgress
                            variant="determinate"
                            value={parseFloat(pct)}
                            sx={{
                              flexGrow: 1,
                              height: 8,
                              borderRadius: 6,
                              bgcolor: "rgba(255,255,255,0.12)",
                              "& .MuiLinearProgress-bar": {
                                bgcolor: theme.palette.primary.main,
                                borderRadius: 6,
                              },
                            }}
                          />

                          <Typography sx={{ minWidth: 48, fontSize: 12, textAlign: "right", color: "white" }}>
                            {entry.count}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>

          {/* Outbreak Prediction */}
          <Box
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              bgcolor: "#273142",
              p: 2,
            }}
          >
            <Stack spacing={1}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "rgba(255,255,255,0.08)",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src={outbreakIcon} alt="Outbreak" style={{ width: 24, height: 24 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} color="white">
                    Outbreak Prediction
                  </Typography>
                </Box>

                <Box
                  sx={{
                    bgcolor: "#EF44443D",
                    color: "#EF4444",
                    px: 2,
                    py: 0.8,
                    borderRadius: 999,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  High Risk
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" color="rgba(255,255,255,0.8)" mb={1}>
                  Contributing Factors
                </Typography>
                <Stack component="ul" spacing={0.8} sx={{ m: 0, pl: 2.5 }}>
                  <Typography component="li" variant="body2" color="rgba(255,255,255,0.7)">
                    Increased positive cases in Luwero district
                  </Typography>
                  <Typography component="li" variant="body2" color="rgba(255,255,255,0.7)">
                    Rising drug resistance rates
                  </Typography>
                  <Typography component="li" variant="body2" color="rgba(255,255,255,0.7)">
                    Heavy rainfall in affected regions
                  </Typography>
                  <Typography component="li" variant="body2" color="rgba(255,255,255,0.7)">
                    Limited access to preventive measures
                  </Typography>
                </Stack>
              </Box>

              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <Typography variant="caption" color="rgba(255,255,255,0.6)">
                    Affected Districts
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1.5} flexWrap="wrap">
                  <Chip label="Luwero" size="small" sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "white" }} />
                  <Chip label="Kalangala" size="small" sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "white" }} />
                  <Chip label="Kibuku" size="small" sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "white" }} />
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Box>

      {/* Malaria Trends */}
      <Box sx={{ mt: 6 }}>
        <Box
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            bgcolor: "#273142",
            p: { xs: 3, md: 4 },
          }}
        >
          <LineChart tests={tests} />
        </Box>
      </Box>

      {/* Recent Activity Table */}
      <Box sx={{ mt: 6 }}>
        <Box sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", bgcolor: "#273142", p: { xs: 2.5, md: 3 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" fontWeight={600} color="white">
              Recent Activity
            </Typography>

            <Box
              component="button"
              onClick={() => navigate("/all-tests")}
              sx={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.2)",
                color: "white",
                px: 1.75,
                py: 0.75,
                borderRadius: 2,
                cursor: "pointer",
                fontSize: 13,
                "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
              }}
            >
              See all
            </Box>
          </Box>

          {/* Table Header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "2.5fr 1.2fr 1.2fr 1.5fr 2fr",
              px: 2,
              py: 1.2,
              borderRadius: 1,
              bgcolor: "rgba(255,255,255,0.06)",
              color: "rgba(255,255,255,0.6)",
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            <Box>Health Center</Box>
            <Box>District</Box>
            <Box>Status</Box>
            <Box>Species</Box>
            <Box>Timestamp</Box>
          </Box>

          {/* Rows */}
          <Box sx={{ mt: 1 }}>
            {recentActivity
              .filter((row) => row.species && row.species.trim().toUpperCase() !== "N/A")
              .map((row, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2.5fr 1.2fr 1.2fr 1.5fr 2fr",
                    px: 2,
                    py: 1.6,
                    alignItems: "center",
                    borderBottom:
                      i !== recentActivity.length - 1
                        ? "1px solid rgba(255,255,255,0.08)"
                        : "none",
                  }}
                >
                  <Typography color="white" fontSize={14}>
                    {row.healthCenter}
                  </Typography>

                  <Typography color="rgba(255,255,255,0.6)" fontSize={14}>
                    {row.district}
                  </Typography>

                  <Box>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1.4,
                        py: 0.4,
                        borderRadius: 999,
                        fontSize: 13,
                        fontWeight: 500,
                        color: row.status === "Positive" ? "#EF4444" : "#5EC16A",
                        bgcolor: row.status === "Positive" ? "#EF44443D" : "#5EC16A3D",
                      }}
                    >
                      {row.status}
                    </Box>
                  </Box>

                  <Typography color="rgba(255,255,255,0.6)" fontSize={14}>
                    {row.species}
                  </Typography>

                  <Typography color="rgba(255,255,255,0.6)" fontSize={14}>
                    {row.timestamp}
                  </Typography>
                </Box>
              ))}
          </Box>
        </Box>
      </Box>

      {/* Fullscreen Map Modal */}
      <Modal open={openMap} onClose={() => setOpenMap(false)}>
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AnimatePresence>
            {openMap && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                style={{
                  width: "90%",
                  height: "90%",
                  backgroundColor: theme.palette.background.default,
                  borderRadius: 12,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="h6" fontWeight={600}>
                    Fullscreen Case Map -{" "}
                    <Typography component="span" color="primary.main" fontWeight={600}>
                      {activeCenters} Active
                    </Typography>{" "}
                    /{" "}
                    <Typography component="span" color="error.main" fontWeight={600}>
                      {healthCenters.length - activeCenters} Offline
                    </Typography>
                  </Typography>

                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>District</InputLabel>
                      <Select value={districtFilter} label="District" onChange={(e) => setDistrictFilter(e.target.value)}>
                        <MenuItem value="All">All</MenuItem>
                        {Array.from(new Set(healthCenters.map((c) => c.district))).map((d) => (
                          <MenuItem key={d} value={d}>{d}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>Status</InputLabel>
                      <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                        <MenuItem value="All">All</MenuItem>
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Offline">Offline</MenuItem>
                      </Select>
                    </FormControl>

                    <IconButton onClick={() => setOpenMap(false)}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <MapComponent centers={filteredCenters} />
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Modal>
    </Box>
  );
};

export default Dashboard;